import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { useLang, useCart, useSettings, fmtDzd, cldUrl, cldSrcSet, API, productPriceDzd, dzdRound } from "../lib/providers";
import { Eye, Flame, Zap, Star, Heart } from "lucide-react";

// Anonymous stable client token for like deduplication
function getClientToken() {
  try {
    let t = localStorage.getItem("dzts_like_token");
    if (!t) { t = crypto.randomUUID(); localStorage.setItem("dzts_like_token", t); }
    return t;
  } catch { return ""; }
}

// Stock suivi à 0 (produit sans variantes, ou toutes variantes à 0) = rupture, même si le statut n'a pas encore été rattrapé par le serveur
export const isSoldOut = (p) => {
  if (!p) return false;
  if (p.stock_status === "out_of_stock") return true;
  if (p.stock_status === "on_order") return false;
  if (p.has_variants && (p.variants || []).length) {
    const c = p.variants.map((v) => v.stock_count);
    return c.every((x) => x !== null && x !== undefined) && c.reduce((a, b) => a + (Number(b) || 0), 0) <= 0;
  }
  return p.stock_count !== null && p.stock_count !== undefined && Number(p.stock_count) <= 0;
};

const STOCK_LABELS = {
  available: { fr: "Disponible", ar: "متوفر", color: "bg-[#22C55E]/20 text-[#22C55E] border-[#22C55E]/40" },
  on_order: { fr: "Sur commande", ar: "حسب الطلب", color: "bg-[var(--c-primary)]/15 text-neon border-neon/40" },
  out_of_stock: { fr: "RUPTURE DE STOCK", ar: "نفذ المخزون", color: "bg-red-600 text-white border-red-700 font-black" },
};

// Deterministic random based on product id + day so the count changes daily but stable per-session
function pseudoViewers(id, min, max) {
  const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
  const seed = `${id}-${day}`;
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const range = Math.max(1, max - min + 1);
  return min + (Math.abs(h) % range);
}

export default function ProductCard({ product, onClick, priority = false, reviewStats = null }) {
  const { lang } = useLang();
  const { settings } = useSettings();
  const dzd = productPriceDzd(product, settings);
  const compareAt = product.compare_at_dzd > 0 ? dzdRound(product.compare_at_dzd) : 0;
  const discountPct = compareAt > dzd ? Math.round(((compareAt - dzd) / compareAt) * 100) : 0;
  const sold = isSoldOut(product);
  const stock = STOCK_LABELS[sold ? "out_of_stock" : product.stock_status] || STOCK_LABELS.available;

  const urgencyOn = settings.urgency_enabled !== false;
  const lowThreshold = settings.urgency_low_stock_threshold || 5;
  const showLow = urgencyOn && !sold && product.stock_count !== null && product.stock_count !== undefined && product.stock_count > 0 && product.stock_count <= lowThreshold;
  const lowLabel = (lang === "ar" ? settings.urgency_label_low_stock_ar : settings.urgency_label_low_stock_fr || "Plus que {n} en stock !").replace("{n}", product.stock_count);

  const viewers = useMemo(() => pseudoViewers(product.id, settings.urgency_viewers_min || 5, settings.urgency_viewers_max || 25), [product.id, settings.urgency_viewers_min, settings.urgency_viewers_max]);
  const showViewers = urgencyOn && !sold && settings.urgency_show_viewers !== false && (settings.urgency_viewers_max || 0) > 0;
  const viewersLabel = (lang === "ar" ? settings.urgency_label_viewers_ar : settings.urgency_label_viewers_fr || "{n} personnes consultent").replace("{n}", viewers);

  // Promo pricing (Phase 22)
  const hasPromo = product.promo_active && Number(product.promo_price_dzd) > 0 && product.promo_price_dzd < dzd;
  const finalPrice = hasPromo ? dzdRound(product.promo_price_dzd) : dzd;
  const promoPct = hasPromo ? Math.round(((dzd - finalPrice) / dzd) * 100) : 0;
  const promoLabel = hasPromo ? (lang === "ar" ? (product.promo_label_ar || product.promo_label_fr) : product.promo_label_fr) : "";

  // Likes (Phase 22)
  const [likes, setLikes] = useState({ liked: false, count: product.likes_count || 0 });
  useEffect(() => {
    const token = getClientToken();
    axios.get(`${API}/products/${product.id}/likes`, { params: { token } })
      .then((r) => setLikes({ liked: !!r.data.liked, count: r.data.likes_count || 0 }))
      .catch(() => {});
  }, [product.id]);

  const toggleLike = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(`${API}/products/${product.id}/like`, { client_token: getClientToken() });
      setLikes({ liked: !!data.liked, count: data.likes_count || 0 });
    } catch { /* silent */ }
  };

  const handleBuy = (e) => {
    e.stopPropagation();
    if (sold) return;
    onClick && onClick(); // toujours ouvrir la modal détail (où l'utilisateur choisit Ajouter au panier ou Achat direct)
  };

  return (
    <div className="group relative themed-card hover:border-neon/40 transition-all duration-300 overflow-hidden cursor-pointer flex flex-col" data-testid={`product-card-${product.id}`} onClick={onClick}>
      <div className="aspect-square bg-black/30 overflow-hidden relative">
        {product.image_url ? (
          <img
            src={cldUrl(product.image_url, { w: 640 })}
            srcSet={cldSrcSet(product.image_url, [320, 480, 640, 960])}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 320px"
            alt={product.name}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${sold ? "grayscale-[50%] opacity-60" : ""}`}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
          />
        ) : (<div className="w-full h-full flex items-center justify-center text-white/30 text-xs">No image</div>)}

        {(sold || product.stock_status === "on_order") && (
          <div className={`absolute bottom-3 start-3 px-2 py-1 text-[11px] font-black uppercase tracking-widest border ${stock.color}`} data-testid={`stock-badge-${product.id}`}>
            {lang === "ar" ? stock.ar : stock.fr}
          </div>
        )}

        {product.is_new && (
          <div className="absolute top-3 start-3 px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-neon text-black border border-black/10 shadow-lg" data-testid={`new-badge-${product.id}`}>
            {lang === "ar" ? "جديد" : "Nouveau"}
          </div>
        )}

        {showLow && (
          <div className={`absolute ${product.is_new ? "top-12" : "top-3"} start-3 px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-clay text-white border border-white/20 flex items-center gap-1 shadow-lg`} data-testid={`urgency-low-${product.id}`}>
            <Flame className="w-3 h-3" />{lowLabel}
          </div>
        )}

        {showViewers && (
          <div className="absolute top-3 end-3 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-black/70 text-neon border border-neon/30 flex items-center gap-1 backdrop-blur-sm" data-testid={`urgency-viewers-${product.id}`}>
            <span className="w-1.5 h-1.5 bg-[#22C55E] rounded-full animate-pulse" />
            <Eye className="w-3 h-3" />{viewersLabel}
          </div>
        )}

        {/* Heart / Like button (always visible top-right when no viewers badge) */}
        <button
          onClick={toggleLike}
          className={`absolute ${showViewers ? "top-12" : "top-3"} end-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm border ${likes.liked ? "border-clay text-clay" : "border-white/20 text-white/70 hover:text-clay hover:border-clay"} transition-colors z-10`}
          data-testid={`like-btn-${product.id}`}
          aria-label={likes.liked ? "Retirer j'aime" : "J'aime"}
          title={likes.liked ? "Retirer" : "J'aime"}
        >
          <Heart className="w-4 h-4" fill={likes.liked ? "#ef4444" : "none"} strokeWidth={2} />
        </button>

        {/* Promo badge */}
        {hasPromo && (
          <div className="absolute bottom-3 end-3 px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-clay text-white border border-white/20 shadow-lg" data-testid={`promo-badge-${product.id}`}>
            {promoLabel || `-${promoPct}%`}
          </div>
        )}
      </div>

      <div className="p-3 sm:p-5 flex-1 flex flex-col">
        <h3 className="font-heading font-semibold sm:font-bold text-sm sm:text-lg sm:uppercase leading-snug sm:leading-tight line-clamp-2 min-h-[2.5rem]">{product.name}</h3>
        {reviewStats && reviewStats.count > 0 && (
          <div className="flex items-center gap-1 mt-1.5" data-testid={`product-rating-${product.id}`} aria-label={`Note ${reviewStats.avg.toFixed(1)} sur 5 — ${reviewStats.count} avis`}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-3.5 h-3.5" fill={i <= Math.round(reviewStats.avg) ? "#a7ff00" : "none"} color={i <= Math.round(reviewStats.avg) ? "#a7ff00" : "#666"} strokeWidth={1.8} />
            ))}
            <span className="text-[10px] text-white/60 font-bold ms-1">
              {reviewStats.avg.toFixed(1)} <span className="text-white/40">({reviewStats.count})</span>
            </span>
          </div>
        )}
        <p className="hidden sm:block text-xs text-white/60 mt-2 line-clamp-2 min-h-[2rem]">{product.description}</p>
        <div className="mt-4 mb-3">
          {(compareAt > 0 && discountPct > 0) && !hasPromo && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-white/40 line-through font-medium" data-testid={`compare-at-${product.id}`}>{fmtDzd(compareAt)}</span>
              <span className="text-[10px] font-black uppercase tracking-widest bg-[#EF4444] text-white px-1.5 py-0.5" data-testid={`discount-pct-${product.id}`}>-{discountPct}%</span>
            </div>
          )}
          {hasPromo && (
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-white/40 line-through font-medium" data-testid={`before-promo-${product.id}`}>{fmtDzd(dzd)}</span>
              <span className="text-[10px] font-black uppercase tracking-widest bg-clay text-white px-1.5 py-0.5">-{promoPct}%</span>
            </div>
          )}
          <div className="font-heading font-black text-xl text-neon" data-testid={`price-dzd-${product.id}`}>{fmtDzd(finalPrice)}</div>
        </div>
        {/* Single CTA: Acheter (opens detail modal where user picks add-to-cart or direct checkout) */}
        <button
          onClick={handleBuy}
          disabled={sold}
          className="mt-auto w-full flex items-center justify-center gap-2 py-3 text-xs font-black uppercase tracking-widest bg-neon text-black hover:opacity-90 disabled:bg-white/10 disabled:text-white/30 disabled:cursor-not-allowed transition-opacity"
          data-testid={`buy-now-${product.id}`}
          aria-label={lang === "ar" ? "اشتر الآن" : "Acheter"}
        >
          <Zap className="w-4 h-4 shrink-0" strokeWidth={2.5} />
          <span>{sold ? (lang === "ar" ? "نفذ" : "Rupture") : (lang === "ar" ? "اشتر الآن" : "Acheter")}</span>
        </button>
      </div>
    </div>
  );
}
